import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import Sidebar from "../Dashboard/Sidebar";
import Topbar from "../Dashboard/Topbar";
import Loader from "../../components/Loader";
import { ToastContainer, toast } from "react-toastify";
import {
  createOrUpdatePatient,
  CreateUpdatePatientPayload,
  getPatientById,
} from "../../api/patient";
import { getClinicsByUser } from "../../api/clinic";
import { useNavigate, useParams } from "react-router-dom";

const PatientCreate: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const observationOptions = [
    "GOOD_ORAL_HYGIENE",
    "FAIR_ORAL_HYGIENE",
    "POOR_ORAL_HYGIENE",
    "NORMAL_ALIGNMENT",
    "CROWDING",
    "SPACING",
    "OVERBITE",
    "UNDERBITE",
    "OPEN_BITE",
    "CROSSBITE",
    "DEEP_BITE",
    "ASYMMETRY_PRESENT",
    "ASYMMETRY_ABSENT",
    "LIP_COMPETENT",
    "LIP_INCOMPETENT",
    "NORMAL_SPEECH",
    "LISP",
    "NASAL_SPEECH",
    "DELAYED_TOOTH_ERUPTION",
    "MIXED_DENTITION",
  ];
  const [formData, setFormData] = useState<CreateUpdatePatientPayload>({
    id: "",
    doctorId: localStorage.getItem("userId") || "",
    clinicId: "",
    name: "",
    profilePictureUrl: "",
    address: "",
    gender: "Male",
    dob: "",
    age: "" as any,
    contact: "",
    email: "",
    patientObservations: [],
    treatmentChoice: "Category1",
  });
  const [errors, setErrors] = useState<{
    clinicId?: string;
    name?: string;
    address?: string;
    dob?: string;
    contact?: string;
    email?: string;
    patientObservations?: string;
  }>({});
  const fetchedClinicsRef = useRef(false);
  const [clinicsLoading, setClinicsLoading] = useState(false);
  const [clinics, setClinics] = useState<{ id: string; name: string }[]>([]);
  const fetchedPatientRef = useRef(false);
  const observationsDropdownRef = useRef<HTMLDivElement | null>(null);
  const [isObservationDropdownOpen, setIsObservationDropdownOpen] =
    useState(false);
  const [patientImagePreview, setPatientImagePreview] = useState<string | null>(
    null
  );
  const [patientImageFile, setPatientImageFile] = useState<File | null>(null);
  useEffect(() => {
    if (fetchedClinicsRef.current) return;
    fetchedClinicsRef.current = true;
    const fetchClinics = async () => {
      setClinicsLoading(true);
      try {
        const res = await getClinicsByUser();
        const list = res?.data?.data || res?.data || [];
        const normalized = (Array.isArray(list) ? list : [])
          .map((c) => {
            const id = c.id || c._id || c.clinicId || c.value || "";
            const name = c.name || c.clinicName || c.label || "";
            return { id, name };
          })
          .filter((c) => c.id && c.name);
        setClinics(normalized);
      } catch (err) {
        setClinics([]);
      } finally {
        setClinicsLoading(false);
      }
    };
    fetchClinics();
  }, []);

  // Prefill when editing existing patient
  useEffect(() => {
    if (!id || fetchedPatientRef.current) return;
    fetchedPatientRef.current = true;
    const fetchPatient = async () => {
      setLoading(true);
      try {
        const res = await getPatientById(id as string);
        const data = res?.data?.data || res?.data;
        if (data) {
          const toDateInput = (iso: string) => {
            try {
              const d = new Date(iso);
              const yyyy = d.getFullYear();
              const mm = String(d.getMonth() + 1).padStart(2, "0");
              const dd = String(d.getDate()).padStart(2, "0");
              return `${yyyy}-${mm}-${dd}`;
            } catch {
              return "";
            }
          };
          const parseObservations = (
            value: unknown,
            fallback: string[]
          ): string[] => {
            if (Array.isArray(value)) {
              return value.filter((item) => typeof item === "string" && item);
            }
            if (typeof value === "string") {
              return value
                .split(",")
                .map((item) => item.trim())
                .filter(Boolean);
            }
            return fallback;
          };
          setFormData((prev) => ({
            ...prev,
            id: data.id || data._id || (id as string),
            doctorId: data.doctorId || prev.doctorId,
            clinicId: data.clinic?.id || data.clinicId || prev.clinicId,
            profilePictureUrl:
              data.profilePictureUrlpath ||
              data.profilePictureUrl ||
              data.image ||
              data.imageUrl ||
              data.photoUrl ||
              prev.profilePictureUrl,
            name:
              data.name ||
              `${data.firstName || ""} ${data.lastName || ""}`.trim() ||
              prev.name,
            address: data.address || prev.address,
            gender: (data.gender ||
              prev.gender) as CreateUpdatePatientPayload["gender"],
            dob: data.dob ? toDateInput(data.dob) : prev.dob,
            age: data.age || prev.age,
            contact: data.contact || data.phone || prev.contact,
            email: data.email || prev.email,
            patientObservations: parseObservations(
              data.patientObservations,
              prev.patientObservations
            ),
            treatmentChoice: data.treatmentChoice || prev.treatmentChoice,
          }));
          const existingImage =
            data.profilePictureUrlpath ||
            data.profilePictureUrl ||
            data.image ||
            data.imageUrl ||
            data.photoUrl ||
            "";
          if (existingImage) {
            setPatientImagePreview(existingImage);
          }
        }
      } catch (err: any) {
        toast.error(err?.response?.data?.message || "Failed to fetch patient");
      } finally {
        setLoading(false);
      }
    };
    fetchPatient();
  }, [id]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        observationsDropdownRef.current &&
        !observationsDropdownRef.current.contains(event.target as Node)
      ) {
        setIsObservationDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleObservationValue = (value: string) => {
    setFormData((prev) => {
      const exists = prev.patientObservations.includes(value);
      const next = exists
        ? prev.patientObservations.filter((obs) => obs !== value)
        : [...prev.patientObservations, value];
      return { ...prev, patientObservations: next };
    });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

    if (name === "contact") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
      setFormData((prev) => ({
        ...prev,
        contact: digitsOnly,
      }));
      if (errors.contact) {
        setErrors((prev) => ({ ...prev, contact: undefined }));
      }
    }
    else if (name === "email") {
      const sanitized = value.replace(/\s/g, "");
      const atCount = (sanitized.match(/@/g) || []).length;

      if (atCount > 1) {
        setErrors((prev) => ({
          ...prev,
          email: "Email cannot contain more than one @",
        }));
        return;
      }

      if (sanitized && !emailRegex.test(sanitized)) {
        setErrors((prev) => ({
          ...prev,
          email: "Please enter a valid email",
        }));
      } else {
        setErrors((prev) => ({ ...prev, email: undefined }));
      }

      setFormData((prev) => ({
        ...prev,
        email: sanitized,
      }));
    }
    else if (name === "dob") {
      const calculatedAge = value ? calculateAge(value) : 0;

      setFormData((prev) => ({
        ...prev,
        dob: value,
        age: calculatedAge,
      }));
    }
    else {
      // IMPORTANT: Keep this
      setFormData((prev) => ({
        ...prev,
        [name]:
          name === "gender"
            ? (value as CreateUpdatePatientPayload["gender"])
            : value,
      }));
    }
  };

  const validate = () => {
    const nextErrors: typeof errors = {};
    if (!formData.clinicId) nextErrors.clinicId = "Clinic is required";
    if (!formData.name.trim()) nextErrors.name = "Patient name is required";
    const emailTrimmed = formData.email.trim();
    if (emailTrimmed) {
      const atCount = (emailTrimmed.match(/@/g) || []).length;
      if (atCount !== 1) {
        nextErrors.email = "Please enter a valid email";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(emailTrimmed)) {
        nextErrors.email = "Please enter a valid email";
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      // Build JSON payload for `data` field
      const dataPayload = {
        id: formData.id || "",
        doctorId: formData.doctorId,
        clinicId: formData.clinicId,
        name: formData.name,
        address: formData.address,
        gender: formData.gender,
        dob: formData.dob || null,
        // age: formData.age || null,
        age: formData.age ? String(formData.age) : null,
        contact: formData.contact || null,
        email: formData.email.trim(),
        patientObservations: formData.patientObservations,
        treatmentChoice: formData.treatmentChoice,
      };

      const formDataToSend = new FormData();
      // Backend expects one JSON object in `data` field
      formDataToSend.append("data", JSON.stringify(dataPayload));

      // And file separately, outside the JSON object, in `profilePictureUrl`
      if (patientImageFile) {
        formDataToSend.append("profilePictureUrl", patientImageFile);
      }

      await createOrUpdatePatient(formDataToSend);
      toast.success("Patient saved successfully");
      navigate(`/patients`);
      setFormData({
        id: "",
        doctorId: "",
        clinicId: "",
        name: "",
        profilePictureUrl: "",
        address: "",
        gender: "Male",
        dob: "",
        age: "" as any,
        contact: "",
        email: "",
        patientObservations: [],
        treatmentChoice: "Category1",
      });
      setPatientImageFile(null);
      setPatientImagePreview(null);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to save patient");
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (dob: string) => {
    const birthDate = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age >= 0 ? age : 0;
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} />
      {loading && <Loader />}
      <Sidebar
        isOpen={isSidebarOpen}
        toggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />

      <div className="flex-1 flex flex-col">
        <Topbar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

        <main className="p-4 md:p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-6xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-full rounded-3xl shadow-2xl bg-white p-8"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                  {id ? "Edit Patient Management" : "Patient Management"}
                </h2>
                <button
                  className="px-4 py-1 rounded-xl bg-gray-100 text-gray-800 border border-gray-200 hover:bg-gray-200 transition flex items-center gap-2"
                  onClick={() => window.history.back()}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-4 h-4"
                    aria-hidden="true"
                  >
                    <path d="M10 19l-7-7 7-7" />
                    <path d="M3 12h18" />
                  </svg>
                  Back
                </button>
              </div>

              <form
                noValidate
                onSubmit={handleSubmit}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >
                <div className="md:col-span-2 flex items-center gap-4 mb-2">
                  <div className="w-20 h-20 rounded-full bg-gray-100 overflow-hidden flex items-center justify-center border border-gray-200">
                    {patientImagePreview ? (
                      <img
                        src={patientImagePreview}
                        alt="Patient"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-xs text-gray-400 text-center px-2">
                        No Image
                      </span>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Patient Image
                    </label>
                    <div className="flex items-center gap-3">
                      <label
                        htmlFor="patientImage"
                        className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm cursor-pointer hover:bg-blue-700 transition"
                      >
                        {patientImagePreview ? "Edit Image" : "Upload Image"}
                      </label>
                      <input
                        id="patientImage"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0] || null;
                          if (!file) return;
                          setPatientImageFile(file);
                          const previewUrl = URL.createObjectURL(file);
                          setPatientImagePreview(previewUrl);
                        }}
                      />
                      {patientImageFile && (
                        <span className="text-xs text-gray-500 truncate max-w-[160px]">
                          {patientImageFile.name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {/* Patient Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Patient Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.name
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : ""
                      }`}
                    required
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                  )}
                </div>
                {/* Clinic */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Clinic <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="clinicId"
                    value={formData.clinicId}
                    onChange={handleChange}
                    required
                    disabled={clinicsLoading}
                    className={`w-full mt-1 p-2 border rounded-lg ${errors.clinicId
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : ""
                      }`}
                  >
                    {clinicsLoading ? (
                      <option value="">Loading clinics...</option>
                    ) : (
                      <>
                        <option value="" disabled>
                          Select Clinic
                        </option>
                        {clinics.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                  {errors.clinicId && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.clinicId}
                    </p>
                  )}
                </div>
                {/* address */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Address
                  </label>
                  <input
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.address
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : ""
                      }`}
                  />
                  {errors.address && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.address}
                    </p>
                  )}
                </div>
                {/* gender */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Gender
                  </label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className="mt-1 w-full p-2 border rounded-xl"
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                {/* DOB */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    name="dob"
                    max={new Date().toISOString().split("T")[0]}
                    value={formData.dob}
                    onChange={handleChange}
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.dob
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : ""
                      }`}
                    required
                  />
                  {errors.dob && (
                    <p className="text-red-500 text-sm mt-1">{errors.dob}</p>
                  )}
                </div>
                {/* Age */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Age (in years)
                  </label>
                  <input
                    type="number"
                    name="age"
                    value={formData.age ?? ""}
                    onChange={handleChange}
                    min="0"
                    step="1"
                    placeholder="Enter Age"
                    disabled={!!formData.dob}
                    className="mt-1 w-full p-2 border rounded-xl"
                    onWheel={(e) => (e.target as HTMLInputElement).blur()}
                  />

                </div>
                {/* contact */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Contact
                  </label>
                  <input
                    name="contact"
                    type="text"
                    value={formData.contact}
                    onChange={handleChange}
                    maxLength={10}
                    placeholder="Enter 10-digit mobile number"
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.contact
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : ""
                      }`}
                  />
                  {errors.contact && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.contact}
                    </p>
                  )}
                </div>
                {/* email */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.email
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : ""
                      }`}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                  )}
                </div>
                {/* patient observations */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Patient Observations
                  </label>
                  <div
                    className="mt-1 relative"
                    ref={observationsDropdownRef}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setIsObservationDropdownOpen((prev) => !prev)
                      }
                      className={`w-full flex flex-wrap gap-2 items-center justify-between border rounded-xl px-3 py-2 text-left ${errors.patientObservations
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-300"
                        }`}
                    >
                      <div className="flex flex-wrap gap-2">
                        {formData.patientObservations.length ? (
                          formData.patientObservations.map((obs) => (
                            <span
                              key={obs}
                              className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full"
                            >
                              {obs.replaceAll("_", " ")}
                            </span>
                          ))
                        ) : (
                          <span className="text-gray-500 text-sm">
                            Select Patient Observations
                          </span>
                        )}
                      </div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={`w-4 h-4 text-gray-500 transition-transform ${isObservationDropdownOpen ? "rotate-180" : ""
                          }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>

                    {isObservationDropdownOpen && (
                      <div className="absolute z-20 mt-2 w-full bg-white border rounded-xl shadow-lg max-h-60 overflow-y-auto">
                        {observationOptions.map((obs) => {
                          const selected =
                            formData.patientObservations.includes(obs);
                          return (
                            <label
                              key={obs}
                              className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                            >
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => toggleObservationValue(obs)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <span className="text-gray-700">
                                {obs.replaceAll("_", " ")}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {errors.patientObservations && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.patientObservations}
                    </p>
                  )}
                </div>

                <div className="md:col-span-2 flex justify-end gap-3 mt-4">
                  <button
                    type="button"
                    className="px-5 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                    onClick={() => window.history.back()}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-5 py-2 rounded-xl  text-white  transition disabled:opacity-60"
                  >
                    {loading ? "Saving..." : "Save Patient"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default PatientCreate;
